import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import AnalyticsServiceSlide from './AnalyticsServiceSlide';
import {
  CHART_COLORS,
  formatDuration,
  getErrorTypeLabel,
  percentTenths,
} from './dashboardSummaryUtils';
import { EXPECTED_HOURS_PER_WEEK, computeUtilization } from './utilizationMath';
import styles from './AnalyticsSummaryModal.module.scss';

// The slide is laid out at a fixed 16:9 size and scaled to fit the viewport, so a
// screenshot of it drops straight onto a PowerPoint page.
const SLIDE_WIDTH = 1280;
const SLIDE_HEIGHT = 720;
const MAX_WEEKS_ON_SLIDE = 8;
const MAX_ERROR_TYPES_ON_SLIDE = 5;
const LANGUAGE_STORAGE_KEY = 'analytics.summaryLanguage';
const PAGES = ['operations', 'service'];

const STRINGS = {
  en: {
    locale: 'en-GB',
    close: 'Close',
    pages: { operations: 'Operation & errors', service: 'Service statistics' },
    eyebrow: 'Fleet analytics',
    title: 'Support overview – calculation of operation and error types',
    trackedTotal: 'Tracked total',
    trackedCaption: 'tracked time in period',
    runTime: 'Run time',
    stability: pct => `${pct} stability`,
    downtime: 'Downtime',
    downtimeShare: pct => `${pct} of tracked time`,
    utilization: 'Utilization',
    utilizationCaption: count =>
      `vs ${EXPECTED_HOURS_PER_WEEK}h/week × ${count} machine${count === 1 ? '' : 's'}`,
    panelErrors: 'Downtime by error type',
    panelUtilization: 'Utilization & weekly view',
    panelMachines: 'Most active machines',
    noDowntime: 'No downtime recorded in this scope.',
    noUsage: 'No usage recorded in this scope.',
    downtimeCallout: value => `Downtime: ${value}`,
    expected: value => `Expected operational: ${value}`,
    trackedRun: value => `Tracked total (run time + downtime): ${value}.`,
    showingWeeks: (shown, total) => ` Showing the last ${shown} of ${total} weeks.`,
    target: `100% target = ${EXPECTED_HOURS_PER_WEEK}h per machine per week`,
    ranked: 'Ranked by tracked run time in the period.',
  },
  sv: {
    locale: 'sv-SE',
    close: 'Stäng',
    pages: { operations: 'Drift & fel', service: 'Servicestatistik' },
    eyebrow: 'Flottanalys',
    title: 'Supportöversikt – beräkning av drift och feltyper',
    trackedTotal: 'Tracked total',
    trackedCaption: 'uppföljd totalperiod',
    runTime: 'Run time',
    stability: pct => `${pct} stabilitet`,
    downtime: 'Downtime',
    downtimeShare: pct => `${pct} av uppföljd tid`,
    utilization: 'Utilization',
    utilizationCaption: count =>
      `mot ${EXPECTED_HOURS_PER_WEEK}h/vecka × ${count} maskin${count === 1 ? '' : 'er'}`,
    panelErrors: 'Downtime per feltyp',
    panelUtilization: 'Utnyttjande & veckobild',
    panelMachines: 'Mest aktiva maskiner',
    noDowntime: 'Ingen downtime registrerad i detta urval.',
    noUsage: 'Ingen användning registrerad i detta urval.',
    downtimeCallout: value => `Downtime: ${value}.`,
    expected: value => `Förväntat operativt: ${value}`,
    trackedRun: value => `Uppföljd totaltid (run time + downtime): ${value}.`,
    showingWeeks: (shown, total) => ` Visar de senaste ${shown} av ${total} veckorna.`,
    target: `100% target (${EXPECTED_HOURS_PER_WEEK}h)`,
    ranked: 'Topplistan visar högst uppmätt aktivitet i perioden.',
  },
};

function loadLanguage() {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored in STRINGS ? stored : 'en';
  } catch {
    return 'en';
  }
}

function formatPercentLocale(ratio, locale) {
  return `${(percentTenths(ratio) / 10).toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
}

function formatDateLabel(dateStr, locale) {
  return new Date(dateStr + 'T00:00:00Z').toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// "3–9 Aug" / "27 Jul – 2 Aug", localized. Weeks start on Monday.
function formatWeekRange(weekStart, locale) {
  const start = new Date(weekStart + 'T00:00:00Z');
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const month = date => date.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' });
  if (start.getUTCMonth() === end.getUTCMonth()) {
    return `${start.getUTCDate()}–${end.getUTCDate()} ${month(start)}`;
  }
  return `${start.getUTCDate()} ${month(start)} – ${end.getUTCDate()} ${month(end)}`;
}

function useSlideScale() {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const update = () => {
      const available = Math.min(
        (window.innerWidth - 48) / SLIDE_WIDTH,
        (window.innerHeight - 140) / SLIDE_HEIGHT
      );
      setScale(Math.max(0.3, Math.min(available, 1.5)));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return scale;
}

function AnalyticsSummaryModal({ usageData, errorData, scope, machineCount, onClose }) {
  const scale = useSlideScale();
  const [language, setLanguage] = useState(loadLanguage);
  const [page, setPage] = useState(PAGES[0]);
  const t = STRINGS[language];
  const pct = ratio => formatPercentLocale(ratio, t.locale);

  const chooseLanguage = next => {
    setLanguage(next);
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
    } catch {
      // Storage unavailable; the choice simply is not remembered.
    }
  };

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        const step = event.key === 'ArrowRight' ? 1 : -1;
        setPage(current => PAGES[PAGES.indexOf(current) + step] || current);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('navigation-open');
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('navigation-open');
    };
  }, [onClose]);

  const trackedMinutes = usageData?.total_minutes || 0;
  const downtimeMinutes = Math.min(errorData?.total_minutes || 0, trackedMinutes);
  const runMinutes = trackedMinutes - downtimeMinutes;
  const stability = trackedMinutes > 0 ? runMinutes / trackedMinutes : 0;
  // Remainder of the rounded stability, so the two shares add up to 100.0%
  const downtimeShare = trackedMinutes > 0 ? (1000 - percentTenths(stability)) / 1000 : 0;

  const utilization = useMemo(
    () =>
      computeUtilization({
        usageData,
        machineCount,
        startDate: scope.startDate,
        endDate: scope.endDate,
      }),
    [usageData, machineCount, scope.startDate, scope.endDate]
  );
  const slideWeeks = utilization.weeks.slice(-MAX_WEEKS_ON_SLIDE);

  const errorTypeRows = useMemo(() => {
    const totalErrorMinutes = errorData?.total_minutes || 0;
    return [...(errorData?.error_type_summary || [])]
      .sort((a, b) => b.total_minutes - a.total_minutes)
      .slice(0, MAX_ERROR_TYPES_ON_SLIDE)
      .map((item, index) => ({
        key: item.error_type,
        label: getErrorTypeLabel(item.error_type),
        minutes: item.total_minutes,
        share: totalErrorMinutes > 0 ? item.total_minutes / totalErrorMinutes : 0,
        color: CHART_COLORS[index % CHART_COLORS.length],
      }));
  }, [errorData]);

  const topMachines = (usageData?.top_machines || []).slice(0, 5);
  const periodLabel = `${formatDateLabel(scope.startDate, t.locale)} – ${formatDateLabel(
    scope.endDate,
    t.locale
  )}`;

  return createPortal(
    <div className={styles.layer} role="presentation">
      <button type="button" className={styles.backdrop} onClick={onClose} aria-label="Close" />
      <div className={styles.toolbar}>
        <div className={styles.languageSwitch} role="group" aria-label="Language">
          {[
            ['en', 'English'],
            ['sv', 'Svenska'],
          ].map(([code, label]) => (
            <button
              type="button"
              key={code}
              className={language === code ? styles.languageActive : ''}
              aria-pressed={language === code}
              onClick={() => chooseLanguage(code)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className={styles.pageTabs} role="tablist" aria-label="Summary page">
          {PAGES.map((key, index) => (
            <button
              type="button"
              key={key}
              role="tab"
              className={page === key ? styles.pageTabActive : ''}
              aria-selected={page === key}
              onClick={() => setPage(key)}
            >
              <span>{index + 1}</span> {t.pages[key]}
            </button>
          ))}
        </div>
        <button type="button" className={styles.closeButton} onClick={onClose}>
          <X size={16} /> {t.close}
        </button>
      </div>
      <div
        className={styles.stage}
        style={{ width: SLIDE_WIDTH * scale, height: SLIDE_HEIGHT * scale }}
      >
        <section
          className={styles.slide}
          style={{ width: SLIDE_WIDTH, height: SLIDE_HEIGHT, transform: `scale(${scale})` }}
          role="dialog"
          aria-modal="true"
          aria-label={page === 'operations' ? t.title : t.pages.service}
          lang={language}
        >
          {/* Both pages stay mounted so switching is instant and the service data is
              already loaded when its tab is opened. */}
          <div className={styles.page} hidden={page !== 'service'}>
            <AnalyticsServiceSlide
              scope={scope}
              language={language}
              locale={t.locale}
              periodLabel={periodLabel}
            />
          </div>
          <div className={styles.page} hidden={page !== 'operations'}>
            <header className={styles.slideHeader}>
              <div>
                <span className={styles.eyebrow}>{t.eyebrow}</span>
                <h2>{t.title}</h2>
              </div>
              <span className={styles.scopeLine}>{periodLabel}</span>
            </header>

            <div className={styles.kpiRow}>
              <div className={`${styles.kpi} ${styles.kpiGreen}`}>
                <span className={styles.kpiLabel}>{t.trackedTotal}</span>
                <strong className={styles.kpiValue}>{formatDuration(trackedMinutes)}</strong>
                <span className={styles.kpiCaption}>{t.trackedCaption}</span>
              </div>
              <div className={`${styles.kpi} ${styles.kpiGreen}`}>
                <span className={styles.kpiLabel}>{t.runTime}</span>
                <strong className={styles.kpiValue}>{formatDuration(runMinutes)}</strong>
                <span className={styles.kpiCaption}>{t.stability(pct(stability))}</span>
              </div>
              <div className={`${styles.kpi} ${styles.kpiRed}`}>
                <span className={styles.kpiLabel}>{t.downtime}</span>
                <strong className={styles.kpiValue}>{formatDuration(downtimeMinutes)}</strong>
                <span className={styles.kpiCaption}>{t.downtimeShare(pct(downtimeShare))}</span>
              </div>
              <div className={`${styles.kpi} ${styles.kpiGreen}`}>
                <span className={styles.kpiLabel}>{t.utilization}</span>
                <strong className={styles.kpiValue}>{pct(utilization.totalUtilization)}</strong>
                <span className={styles.kpiCaption}>{t.utilizationCaption(machineCount)}</span>
              </div>
            </div>

            <div className={styles.panelRow}>
              <div className={styles.panel}>
                <h3>
                  <span>01</span> {t.panelErrors}
                </h3>
                <div className={styles.panelBody}>
                  {errorTypeRows.length === 0 && <p className={styles.empty}>{t.noDowntime}</p>}
                  {errorTypeRows.map(row => (
                    <div key={row.key} className={styles.barRow}>
                      <div className={styles.barHeader}>
                        <span>{row.label}</span>
                        <strong>{formatDuration(row.minutes)}</strong>
                      </div>
                      <div className={styles.barTrack}>
                        <div
                          className={styles.barFill}
                          style={{ width: `${row.share * 100}%`, backgroundColor: row.color }}
                        />
                      </div>
                      <small>{pct(row.share)}</small>
                    </div>
                  ))}
                </div>
                <div className={styles.callout}>
                  {t.downtimeCallout(formatDuration(downtimeMinutes))}
                </div>
              </div>

              <div className={styles.panel}>
                <h3>
                  <span>02</span> {t.panelUtilization}
                </h3>
                <div className={styles.panelBody}>
                  <p className={styles.lead}>
                    {t.expected(formatDuration(Math.round(utilization.totalExpectedMinutes)))}
                  </p>
                  <p className={styles.sub}>
                    {t.trackedRun(formatDuration(utilization.totalRunMinutes))}
                    {utilization.weeks.length > slideWeeks.length
                      ? t.showingWeeks(slideWeeks.length, utilization.weeks.length)
                      : ''}
                  </p>
                  {slideWeeks.length === 0 && <p className={styles.empty}>{t.noUsage}</p>}
                  {slideWeeks.map(week => (
                    <div key={week.weekStart} className={styles.weekRow}>
                      <span className={styles.weekLabel}>
                        {formatWeekRange(week.weekStart, t.locale)}
                      </span>
                      <div className={styles.barTrack}>
                        <div
                          className={`${styles.barFill} ${styles.barGreen}`}
                          style={{ width: `${Math.min(week.utilization, 1) * 100}%` }}
                        />
                      </div>
                      <strong className={styles.weekValue}>{pct(week.utilization)}</strong>
                    </div>
                  ))}
                </div>
                <p className={styles.footnote}>{t.target}</p>
              </div>

              <div className={styles.panel}>
                <h3>
                  <span>03</span> {t.panelMachines}
                </h3>
                <div className={styles.panelBody}>
                  {topMachines.length === 0 && <p className={styles.empty}>{t.noUsage}</p>}
                  {topMachines.map((machine, index) => (
                    <div key={machine.machine_id} className={styles.machineRow}>
                      <span className={styles.rank}>#{index + 1}</span>
                      <div className={styles.machineInfo}>
                        <strong>{machine.nickname || machine.serial}</strong>
                        {machine.nickname && <small>{machine.serial}</small>}
                      </div>
                      <strong className={styles.machineValue}>
                        {formatDuration(machine.total_minutes)}
                      </strong>
                    </div>
                  ))}
                </div>
                <p className={styles.footnote}>{t.ranked}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>,
    document.body
  );
}

export default AnalyticsSummaryModal;
